/**
 * Type definitions for i.MX USB Loader
 * Based on the NXP i.MX Serial Download Protocol (SDP)
 */

// ============================================================================
// SDP Protocol Types
// ============================================================================

/**
 * SDP Command codes
 */
export enum SDPCommand {
  READ_REG = 0x0101,
  WRITE_REG = 0x0202,
  WRITE_FILE = 0x0404,
  ERROR_STATUS = 0x0505,
  WRITE_DCD = 0x0a0a,
  JUMP_ADDRESS = 0x0b0b,
}

/**
 * File types for WRITE_FILE command
 */
export enum FileType {
  APP = 0xaa,
  CSF = 0xcc,
  DCD = 0xee,
  LOAD_ONLY = 0x00,
}

/**
 * HID Report IDs
 */
export enum HIDReportID {
  CMD = 1, // Command out
  DATA_OUT = 2, // Data out
  SECURITY = 3, // Security state in
  DATA_IN = 4, // Data in
}

/**
 * HAB (High Assurance Boot) security modes
 */
export enum HABSecurityMode {
  PRODUCTION = 0x12343412,
  DEVELOPMENT = 0x56787856,
}

// ============================================================================
// Device Configuration Types
// ============================================================================

/**
 * Device identification by VID:PID
 */
export interface DeviceIdentifier {
  vendorId: number;
  productId: number;
}

/**
 * Work item configuration - describes what to load and how
 */
export interface WorkItem {
  filename: string;
  dcd?: boolean; // Process DCD table if present
  loadAddr?: number; // Load address for raw files
  jump?: boolean | 'header'; // Jump after loading
  jumpAddr?: number; // Explicit jump address
}

/**
 * RAM area definition
 */
export interface RAMArea {
  start: number;
  size: number;
}

/**
 * Device configuration loaded from .conf files
 */
export interface DeviceConfig {
  name: string;
  mode: 'HID' | 'BULK';
  headerType: 'MX53' | 'MX8M';
  maxTransfer: number;
  dcdAddr: number;
  ramAreas: RAMArea[];
  work: WorkItem[];
}

/**
 * Device configuration chain for multi-stage boot
 * Example: [[ROM VID/PID, config], [U-Boot VID/PID, config]]
 */
export type DeviceConfigChain = Array<[number, number, string]>;

// ============================================================================
// Boot Script Types (U-Boot mkimage format)
// ============================================================================

/**
 * U-Boot mkimage header structure
 * Used for boot script files (.scr)
 */
export interface MkimageHeader {
  magic: number; // 0x27051956
  headerCRC: number; // CRC32 of header (with this field as 0)
  timestamp: number; // Image creation time
  dataSize: number; // Data payload size
  loadAddr: number; // Load address
  entryPoint: number; // Entry point address
  dataCRC: number; // CRC32 of data payload
  os: number; // Operating system
  arch: number; // Architecture
  imageType: number; // Image type (script = 6)
  compression: number; // Compression type
  name: string; // Image name (max 32 bytes)
}

// ============================================================================
// IVT (Image Vector Table) Types
// ============================================================================

/**
 * IVT Header structure (i.MX boot image header)
 */
export interface IVTHeader {
  tag: number; // 0xD1
  length: number;
  version: number; // 0x40 or 0x41
  startAddr: number; // Entry point
  reserved1: number;
  dcdPtr: number; // DCD table pointer (or 0)
  bootDataPtr: number; // Boot data pointer
  selfPtr: number; // IVT location in memory
  csfPtr: number; // CSF pointer (or 0)
  reserved2: number;
}

/**
 * Boot data structure
 */
export interface BootData {
  dest: number; // Destination address
  imageLen: number; // Image length
  plugin: number; // Plugin flag
  offset: number; // Offset in file
}

// ============================================================================
// Transfer and Protocol Types
// ============================================================================

/**
 * Result of a file transfer operation
 */
export interface TransferResult {
  bytesTransferred: number;
  success: boolean;
  error?: Error;
}

/**
 * Result of processing an IMX file
 */
export interface ProcessResult {
  success: boolean;
  ivt?: IVTHeader;
  bootData?: BootData;
  dcdProcessed: boolean;
  bytesLoaded: number;
  jumped: boolean;
  error?: Error;
}

/**
 * Device status response
 */
export interface DeviceStatus {
  habSecurity: HABSecurityMode;
  habSecurityString: 'production' | 'development';
  status?: number;
}

// ============================================================================
// WebHID Abstraction Types
// ============================================================================

/**
 * WebHID device interface (for mocking/testing)
 */
export interface IHIDDevice {
  readonly vendorId: number;
  readonly productId: number;
  readonly productName: string | undefined;
  readonly opened: boolean;

  open(): Promise<void>;
  close(): Promise<void>;
  sendReport(reportId: number, data: BufferSource): Promise<void>;
  addEventListener(
    type: 'inputreport',
    listener: (event: HIDInputReportEvent) => void
  ): void;
  removeEventListener(
    type: 'inputreport',
    listener: (event: HIDInputReportEvent) => void
  ): void;
}

/**
 * HID Input Report Event
 */
export interface HIDInputReportEvent {
  reportId: number;
  data: DataView;
}

/**
 * Report waiter for promise-based report handling
 */
export interface ReportWaiter {
  resolve: (data: Uint8Array) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

// ============================================================================
// Logger Interface
// ============================================================================

/**
 * Log levels
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

/**
 * Log entry
 */
export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  data?: unknown;
}

/**
 * Logger interface for dependency injection
 */
export interface ILogger {
  log(message: string, level?: LogLevel, data?: unknown): void;
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  success(message: string, data?: unknown): void;
  warning(message: string, data?: unknown): void;
  error(message: string, data?: unknown): void;
  getEntries(): LogEntry[];
  clear(): void;
  download(): void;
  uploadToBackend(sessionId: string): Promise<void>;
}

// ============================================================================
// Progress Tracking
// ============================================================================

/**
 * Progress update callback
 */
export type ProgressCallback = (percent: number, message?: string) => void;

// ============================================================================
// Device Constants
// ============================================================================

/**
 * Known i.MX device identifiers for G5 force plates
 * These are used for WebHID device filtering
 */
export interface IMXDeviceInfo {
  vid: number;
  pid: number;
  name: string;
  mode: 'ROM' | 'U-Boot';
}

/**
 * List of known i.MX devices for G5 force plates
 * VID 0x15A2 = Freescale/NXP (i.MX 6ULL ROM mode)
 * VID 0x1FC9 = NXP Semiconductors (i.MX8M ROM mode)
 * VID 0x1B67 = U-Boot USB download gadget
 */
export const IMX_DEVICES: IMXDeviceInfo[] = [
  // i.MX 6ULL in ROM serial download mode (SE Blank 6ULL)
  { vid: 0x15a2, pid: 0x0080, name: 'G5 Force Plate (i.MX 6ULL ROM)', mode: 'ROM' },
  // i.MX8M Mini in ROM serial download mode
  { vid: 0x1fc9, pid: 0x0134, name: 'G5 Force Plate (ROM Mode)', mode: 'ROM' },
  // i.MX8M Mini alternate ROM mode PID
  { vid: 0x1fc9, pid: 0x013e, name: 'G5 Force Plate (ROM Mode Alt)', mode: 'ROM' },
  // U-Boot USB download gadget mode (after bootloader loads)
  { vid: 0x1b67, pid: 0x4000, name: 'USB download gadget', mode: 'U-Boot' },
];

// ============================================================================
// HID Navigator Interface (for Dependency Injection)
// ============================================================================

/**
 * HID Device Filter for requestDevice
 */
export interface IHIDDeviceFilter {
  vendorId?: number;
  productId?: number;
  usagePage?: number;
  usage?: number;
}

/**
 * HID Device Request Options
 */
export interface IHIDDeviceRequestOptions {
  filters: IHIDDeviceFilter[];
}

/**
 * HID Connection Event
 */
export interface IHIDConnectionEvent {
  device: IHIDDevice;
}

/**
 * HID Navigator interface (abstracts navigator.hid)
 *
 * This interface allows injecting mock implementations for testing
 * without requiring physical HID devices.
 */
export interface IHIDNavigator {
  requestDevice(options: IHIDDeviceRequestOptions): Promise<IHIDDevice[]>;
  getDevices(): Promise<IHIDDevice[]>;
  addEventListener(
    type: 'connect' | 'disconnect',
    listener: (event: IHIDConnectionEvent) => void
  ): void;
  removeEventListener(
    type: 'connect' | 'disconnect',
    listener: (event: IHIDConnectionEvent) => void
  ): void;
}

/**
 * Default HID Navigator that wraps the real navigator.hid API
 */
export function createBrowserHIDNavigator(): IHIDNavigator | null {
  if (typeof navigator === 'undefined' || !('hid' in navigator)) {
    return null;
  }

  const hid = navigator.hid;

  return {
    async requestDevice(options: IHIDDeviceRequestOptions): Promise<IHIDDevice[]> {
      const devices = await hid.requestDevice(options as HIDDeviceRequestOptions);
      return devices.map(wrapHIDDevice);
    },
    async getDevices(): Promise<IHIDDevice[]> {
      const devices = await hid.getDevices();
      return devices.map(wrapHIDDevice);
    },
    addEventListener(
      type: 'connect' | 'disconnect',
      listener: (event: IHIDConnectionEvent) => void
    ): void {
      hid.addEventListener(type, (event: HIDConnectionEvent) => {
        listener({ device: wrapHIDDevice(event.device) });
      });
    },
    removeEventListener(
      type: 'connect' | 'disconnect',
      listener: (event: IHIDConnectionEvent) => void
    ): void {
      // Note: This won't properly remove the listener due to the wrapper
      // For production use, consider a more robust implementation
      hid.removeEventListener(type, listener as any);
    },
  };
}

/**
 * Wrap native HIDDevice in IHIDDevice interface
 *
 * This function wraps a native browser HIDDevice in the IHIDDevice interface,
 * providing a consistent API for device communication across the application.
 */
export function wrapHIDDevice(device: HIDDevice): IHIDDevice {
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    productName: device.productName || undefined,
    opened: device.opened,
    open: () => device.open(),
    close: () => device.close(),
    sendReport: (reportId: number, data: BufferSource) =>
      device.sendReport(reportId, data),
    addEventListener: (type: 'inputreport', listener: any) =>
      device.addEventListener(type, listener),
    removeEventListener: (type: 'inputreport', listener: any) =>
      device.removeEventListener(type, listener),
  };
}
